import {
  createTokenRefreshInterceptor,
  type RequestService,
  transformKeysToCamelCase,
} from '@metapic-apps/shared'
import type { AxiosError, AxiosRequestConfig } from 'axios'
import axios from 'axios'
import axiosRetry from 'axios-retry'

import { notifyUserBanned } from './banned-user.service'
import { setNotificationsUnavailable } from './notification-availability.service'

import { config } from '@/lib/helpers'
import { observeAdBlockerFailure } from '@/lib/services/ad-blocker.service'
import { useAuthStore } from '@/stores/auth.stores'

type Params = {
  isFormData?: boolean
  isForcedRetryOnError?: boolean
}
type RequestConfig<T = unknown> = AxiosRequestConfig<T> & Params

type ClientOptions = {
  shouldTransformKeysToCamelCase?: boolean
  withInterceptors?: boolean
  trackAvailability?: boolean
  detectAdBlocker?: boolean
}

let demoAdapter:
  | ((config: import('axios').InternalAxiosRequestConfig) => Promise<import('axios').AxiosResponse>)
  | undefined
if (import.meta.env.VITE_DEMO_MODE === 'true') {
  const { createDemoAdapter } = await import('@/demo/demo-adapter')
  demoAdapter = createDemoAdapter()
}

const createApiClient = <FormatCamelCaseByDefault extends boolean = false>(
  baseURL: string,
  options: ClientOptions,
): RequestService<FormatCamelCaseByDefault> => {
  const apiClient = axios.create({
    baseURL,
  })

  if (demoAdapter) {
    apiClient.defaults.adapter = demoAdapter
  }

  axiosRetry(apiClient, {
    retries: 2,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: (error: AxiosError) => {
      const status = error.response?.status
      const config = error.config as RequestConfig | undefined
      const method = config?.method
      const isForcedRetryOnError = config?.isForcedRetryOnError

      const isSafeMethod = method === 'get' || method === 'head'
      const isNetworkError = !error.response
      const isSafeToRetryStatus = status && (status === 429 || status >= 500)

      if (isForcedRetryOnError && isSafeToRetryStatus) {
        return true
      }
      if (isForcedRetryOnError === false) {
        return false
      }

      if (isSafeMethod && (isNetworkError || isSafeToRetryStatus)) {
        return true
      }

      return false
    },
  })

  if (options.withInterceptors) {
    apiClient.interceptors.request.use(
      (request) => {
        const authStore = useAuthStore()

        request.headers.setAuthorization(authStore.authorization || '')
        request.headers.setContentType(
          request.isFormData ? 'multipart/form-data' : 'application/json',
        )

        return request
      },
      (error: AxiosError) => Promise.reject(error),
    )

    apiClient.interceptors.response.use(
      (response) => {
        if (
          response.config.shouldTransformKeysToCamelCase ??
          options?.shouldTransformKeysToCamelCase
        ) {
          return {
            ...response,
            data: transformKeysToCamelCase(response.data),
          }
        }
        return response
      },
      (
        error: AxiosError<{
          code?: string
          message?: string
        }>,
      ) => {
        if (error.response?.status === 403 && error.response?.data?.code === 'USER_BANNED') {
          notifyUserBanned()
        }

        return Promise.reject(error)
      },
    )

    createTokenRefreshInterceptor(apiClient, {
      refreshToken: () => useAuthStore().refreshToken(),
      onRefreshFailure: () => useAuthStore().handleRefreshFailure(),
    })
  }

  if (options.trackAvailability) {
    apiClient.interceptors.response.use(
      (response) => {
        setNotificationsUnavailable(false)
        return response
      },
      (error: AxiosError) => {
        const isNetworkError = !error.response
        const isServerError = (error.response?.status ?? 0) >= 500
        if (isNetworkError || isServerError) {
          setNotificationsUnavailable(true)
        }
        return Promise.reject(error)
      },
    )
  }

  if (options.detectAdBlocker) {
    apiClient.interceptors.response.use(undefined, (error: AxiosError) => {
      void observeAdBlockerFailure(error)
      return Promise.reject(error)
    })
  }

  return {
    get(resource: string, config?: RequestConfig) {
      return apiClient.get(resource, config)
    },
    post<T>(resource: string, data: T, config?: RequestConfig<T>) {
      return apiClient.post(resource, data, config)
    },
    put<T>(resource: string, data: T, config?: RequestConfig<T>) {
      return apiClient.put(resource, data, config)
    },
    delete(resource: string, config?: RequestConfig) {
      return apiClient.delete(resource, config)
    },
    patch<T>(resource: string, data: T, config?: RequestConfig<T>) {
      return apiClient.patch(resource, data, config)
    },
    getBlob: (resource: string, config?: RequestConfig) => {
      const updatedConfig = { ...(config || {}), responseType: 'blob' as const }
      return apiClient.get(resource, updatedConfig)
    },
  }
}

export const request = createApiClient(config.metapicApiUrl, {
  withInterceptors: true,
  detectAdBlocker: true,
})
export const notificationRequest = createApiClient(config.notificationApiUrl, {
  withInterceptors: true,
  trackAvailability: true,
})
export const externalApiRequest = createApiClient('', { withInterceptors: false })
